/*
* Palo Alto Theme
*
* Use this file to add custom Javascript to Palo Alto.  Keeping your custom
* Javascript in this fill will make it easier to update Palo Alto. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  // ========================================
  // Bulk Quantity Selector with Custom ATC Button
  // ========================================
  // This uses a custom ATC button with unique selectors to prevent
  // third-party scripts from overwriting the bulk-discounted prices.
  
  /**
   * Format price in cents to money format
   */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    
    // Use theme's formatMoney if available
    if (window.theme && window.theme.formatMoney) {
      const format = window.theme.settings && window.theme.settings.currency_code_enable 
        ? window.theme.moneyWithCurrencyFormat 
        : window.theme.moneyFormat;
      return window.theme.formatMoney(cents, format);
    }
    
    // Fallback formatting
    return '$' + (cents / 100).toFixed(2);
  }
  
  /**
   * Get pricing info for a variant, including regular price and subscription price
   * Returns: { regularPrice, currentPrice, hasSubscription }
   * 
   * Formula:
   * - subscription discounted price = regular price - (regular price * subscription discount %)
   * - bulk discount price = subscription discounted price - (subscription discounted price * bulk discount %)
   */
  function getPricingInfo(form) {
    const productContainer = form.closest('[data-product]') || form.closest('.product') || form.closest('.popup-quick-view');
    if (!productContainer) return null;
    
    const productJsonEl = productContainer.querySelector('[data-product-json]');
    if (!productJsonEl) return null;
    
    try {
      const productData = JSON.parse(productJsonEl.textContent);
      const variantId = parseInt(form.querySelector('input[name="id"]')?.value);
      const variant = productData.variants.find(v => v.id === variantId);
      
      if (!variant) return null;
      
      const regularPrice = variant.price;
      
      let sellingPlanValue = null;
      let isSubscriptionActive = false;
      
      // Method 1: Recharge widget - check if "Subscribe & save" option is active
      // Recharge uses radio buttons with name like "selling_plan_PRODUCTID" and 
      // marks the active option with .rc_widget__option--active class
      const rechargeSubsaveOption = productContainer.querySelector('.rc-option__subsave, [data-option-subsave]');
      if (rechargeSubsaveOption) {
        const isRechargeSubActive = rechargeSubsaveOption.classList.contains('rc_widget__option--active') 
          || rechargeSubsaveOption.classList.contains('rc-option--active');
        
        if (isRechargeSubActive) {
          const sellingPlanSelect = productContainer.querySelector('select[name="selling_plan"]');
          if (sellingPlanSelect && sellingPlanSelect.value) {
            sellingPlanValue = sellingPlanSelect.value;
            isSubscriptionActive = true;
          }
        }
      }
      
      // Method 2: Look for INPUT element with value (theme native or other apps)
      if (!isSubscriptionActive) {
        const sellingPlanInput = form.querySelector('input[name="selling_plan"]') 
          || productContainer.querySelector('input[name="selling_plan"]');
        
        if (sellingPlanInput) {
          if (sellingPlanInput.type === 'hidden' && sellingPlanInput.value) {
            sellingPlanValue = sellingPlanInput.value;
            isSubscriptionActive = true;
          } else if (sellingPlanInput.type === 'radio') {
            const checkedPlan = productContainer.querySelector('input[name="selling_plan"]:checked');
            if (checkedPlan && checkedPlan.value) {
              sellingPlanValue = checkedPlan.value;
              isSubscriptionActive = true;
            }
          }
        }
      }
      
      // Method 3: Check theme's native subscription group toggle
      if (!isSubscriptionActive) {
        const subscriptionContainer = productContainer.querySelector('[data-subscription-selectors]');
        const groupToggle = subscriptionContainer?.querySelector('input[name="selling-plan-group"]:checked');
        
        if (groupToggle && groupToggle.value !== '') {
          const checkedPlan = subscriptionContainer.querySelector('input[name="selling_plan"]:checked');
          if (checkedPlan && checkedPlan.value) {
            sellingPlanValue = checkedPlan.value;
            isSubscriptionActive = true;
          }
        }
      }
      
      const hasActiveSubscription = isSubscriptionActive && !!sellingPlanValue && sellingPlanValue !== '' && sellingPlanValue !== '0';
      
      if (hasActiveSubscription && sellingPlanValue) {
        const planId = parseInt(sellingPlanValue);
        
        if (variant.selling_plan_allocations) {
          const allocation = variant.selling_plan_allocations.find(
            a => a.selling_plan_id === planId
          );
          
          if (allocation) {
            // per_delivery_price already has subscription discount applied
            return {
              regularPrice: regularPrice,
              currentPrice: allocation.per_delivery_price,
              hasSubscription: true
            };
          }
        }
      }
      
      return {
        regularPrice: regularPrice,
        currentPrice: regularPrice,
        hasSubscription: false
      };
    } catch (e) {
      return null;
    }
  }
  
  /**
   * Update the quantity selector button prices based on subscription state
   * When subscription is selected, show subscription-discounted price with bulk discount
   */
  function updateQuantityButtonPrices(form) {
    const productContainer = form.closest('[data-product]') || form.closest('.product');
    if (!productContainer) return;
    
    const selector = productContainer.querySelector('[data-quantity-selector]');
    if (!selector) return;
    
    const buttons = selector.querySelectorAll('[data-quantity-btn]');
    const pricingInfo = getPricingInfo(form);
    if (!pricingInfo) return;
    
    const { currentPrice } = pricingInfo;
    
    buttons.forEach((button) => {
      const discount = parseInt(button.getAttribute('data-discount'), 10) || 0;
      const priceEl = button.querySelector('[data-btn-price]');
      
      if (priceEl) {
        // Apply bulk discount on top of current price (which may already have subscription discount)
        const discountedPrice = Math.round(currentPrice * (100 - discount) / 100);
        priceEl.textContent = formatMoney(discountedPrice);
      }
    });
  }
  
  /**
   * Update the custom bulk ATC button with the calculated price
   * 
   * Price calculation follows:
   * 1. subscription discounted price = regular price - (regular price * subscription discount %)
   * 2. bulk discount price = subscription discounted price - (subscription discounted price * bulk discount %)
   * 3. final total = bulk discount price * quantity
   */
  function updateBulkATCButton(form, quantity, bulkDiscountPercent) {
    const productContainer = form.closest('[data-product]') || form.closest('.product');
    if (!productContainer) return;
    
    const originalButton = form.querySelector('[data-add-to-cart]');
    const bulkButton = form.querySelector('[data-bulk-atc-button]');
    
    if (!bulkButton) return;
    
    const hasBulkDiscount = bulkDiscountPercent > 0;
    
    // Get pricing info (includes regular price and subscription-discounted price)
    const pricingInfo = getPricingInfo(form);
    if (!pricingInfo) return;
    
    const { regularPrice, currentPrice } = pricingInfo;
    
    if (hasBulkDiscount) {
      // Apply bulk discount ON TOP of subscription discount (if any)
      // Formula: bulk discount price = subscription discounted price - (subscription discounted price * bulk discount %)
      const bulkDiscountedUnitPrice = Math.round(currentPrice * (100 - bulkDiscountPercent) / 100);
      
      // Calculate totals
      const finalTotal = bulkDiscountedUnitPrice * quantity;
      
      // Compare price shows the regular price (before ANY discounts) × quantity
      // This shows the full savings from both subscription + bulk
      const compareTotal = regularPrice * quantity;
      
      // Update bulk button prices
      const priceEl = bulkButton.querySelector('.bulk-atc-button__price');
      const comparePriceEl = bulkButton.querySelector('.bulk-atc-button__compare-price');
      
      if (priceEl) {
        priceEl.textContent = formatMoney(finalTotal);
      }
      
      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoney(compareTotal);
        comparePriceEl.classList.remove('hidden');
      }
      
      // Show bulk button, hide original
      if (originalButton) originalButton.style.display = 'none';
      bulkButton.style.display = '';
      
    } else {
      // No bulk discount - show original button, hide bulk button
      if (originalButton) originalButton.style.display = '';
      bulkButton.style.display = 'none';
    }
  }
  
  /**
   * Initialize bulk quantity selector functionality
   */
  function initQuantitySelector() {
    const quantitySelectors = document.querySelectorAll('[data-quantity-selector]');
    
    quantitySelectors.forEach((selector) => {
      // Skip if already initialized
      if (selector.hasAttribute('data-bulk-initialized')) return;
      selector.setAttribute('data-bulk-initialized', 'true');
      
      const buttons = selector.querySelectorAll('[data-quantity-btn]');
      
      // Find the associated product form
      const productContainer = selector.closest('[data-product]') || selector.closest('.product');
      const productForm = productContainer?.querySelector('[data-product-form]')
        || document.querySelector('[data-product-form]');
      
      if (!productForm) return;
      
      // Track current state
      let currentQuantity = 1;
      let currentDiscount = 0;
      
      // Get initial state from active button
      const activeButton = selector.querySelector('.quantity-selector-btn--active');
      if (activeButton) {
        currentQuantity = parseInt(activeButton.getAttribute('data-quantity'), 10) || 1;
        currentDiscount = parseInt(activeButton.getAttribute('data-discount'), 10) || 0;
      }
      
      // Initial update
      updateBulkATCButton(productForm, currentQuantity, currentDiscount);
      updateQuantityButtonPrices(productForm);
      
      buttons.forEach((button) => {
        button.addEventListener('click', function(e) {
          e.preventDefault();
          
          // Get quantity and discount from data attributes
          const quantity = parseInt(this.getAttribute('data-quantity'), 10) || 1;
          const discount = parseInt(this.getAttribute('data-discount'), 10) || 0;
          
          currentQuantity = quantity;
          currentDiscount = discount;
          
          // Update button active states
          buttons.forEach(btn => btn.classList.remove('quantity-selector-btn--active'));
          this.classList.add('quantity-selector-btn--active');
          
          // Update the quantity input field
          const quantityInput = productForm.querySelector('input[name="quantity"]');
          if (quantityInput) {
            quantityInput.value = quantity;
            
            // Trigger events for theme compatibility
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          }
          
          // Update the bulk ATC button price
          updateBulkATCButton(productForm, quantity, discount);
        });
      });
      
      // Listen for variant changes to update price
      productContainer?.addEventListener('theme:variant:change', function() {
        setTimeout(() => {
          updateBulkATCButton(productForm, currentQuantity, currentDiscount);
          updateQuantityButtonPrices(productForm);
        }, 50);
      });
      
      // Listen for variant ID input changes to catch option selector changes
      // This ensures prices update when size/color options change
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.addEventListener('change', function() {
          setTimeout(() => {
            updateBulkATCButton(productForm, currentQuantity, currentDiscount);
            updateQuantityButtonPrices(productForm);
          }, 50);
        });
      }
      
      // Listen for subscription plan changes (supports theme native and other apps)
      const subscriptionElements = [
        ...productForm.querySelectorAll('input[name="selling_plan"], input[name="selling-plan-group"]'),
        ...productContainer.querySelectorAll('select[name="selling_plan"]'),
        ...productContainer.querySelectorAll('input[name="selling_plan"]')
      ];
      subscriptionElements.forEach(el => {
        el.addEventListener('change', function() {
          setTimeout(() => {
            updateBulkATCButton(productForm, currentQuantity, currentDiscount);
            updateQuantityButtonPrices(productForm);
          }, 50);
        });
      });
      
      // Use EVENT DELEGATION for Recharge widget (it loads dynamically after our script)
      // Listen for clicks anywhere in the product container and check if it's a Recharge element
      productContainer.addEventListener('click', function(e) {
        const target = e.target;
        
        // Check if clicked element or its parents are Recharge subscription options
        const rechargeOption = target.closest('.rc-option, .rc_widget__option, [data-option-onetime], [data-option-subsave]');
        const rechargeLabel = target.closest('.rc-radio__label, .rc_widget__option__label');
        const rechargeInput = target.closest('input[name^="selling_plan_"]');
        
        if (rechargeOption || rechargeLabel || rechargeInput) {
          // Wait for Recharge to update its state, then update our buttons
          setTimeout(() => {
            updateBulkATCButton(productForm, currentQuantity, currentDiscount);
            updateQuantityButtonPrices(productForm);
          }, 150);
        }
      });
      
      // Also listen for change events on the container (for radio/select changes)
      productContainer.addEventListener('change', function(e) {
        const target = e.target;
        
        // Check if it's a Recharge radio or select
        if (target.name && (target.name.startsWith('selling_plan') || target.name === 'selling_plan')) {
          setTimeout(() => {
            updateBulkATCButton(productForm, currentQuantity, currentDiscount);
            updateQuantityButtonPrices(productForm);
          }, 100);
        }
      });
      
      // Form submit handler - ensure quantity is set correctly
      productForm.addEventListener('submit', function() {
        const quantityInput = this.querySelector('input[name="quantity"]');
        if (quantityInput && currentQuantity > 1) {
          quantityInput.value = currentQuantity;
        }
      });
    });
  }
  
  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuantitySelector);
  } else {
    initQuantitySelector();
  }
  
  // Re-initialize when Shopify section is rendered (for theme editor)
  document.addEventListener('shopify:section:load', initQuantitySelector);
  
  // Re-initialize when quick view popup opens
  document.addEventListener('theme:quick-view:loaded', initQuantitySelector);

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
